import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../../../component/context/AuthContext";
import * as CONST from "../../../component/config/Constant";
import { toast } from "react-toastify";
import "../../../assets/css/table.css";
import ViewSubmissionModal from "./ViewSubmissionModal";
import { decrypt } from "../../quiz/Helper";
import DataTable from "react-data-table-component";
import HeaderSection from "../banner/HeaderSection";
import DivLoader from "../../../component/utilities/Spinner";
import { ViewIcon } from '../../../component/utilities/ActionIcons';
import Loading from "react-fullscreen-loading";

export default function SubmissionsList() {
  const [authUserData] = useContext(AuthContext);
  const token = authUserData && authUserData[0] ? authUserData[0].token : "";
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fullLoading, setFullLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);

  // Search states
  const [search, setSearch] = useState("");
  const [searchDate, setSearchDate] = useState("");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const { search: searchParams } = useLocation();
  const query = new URLSearchParams(searchParams);
  const rawSetId = query.get("setId") || "";
  const initialSetId = decrypt(rawSetId) || rawSetId;

  const [maxPageLimit, setMaxPageLimit] = useState(10);
  const [minPageLimit, setMinPageLimit] = useState(0);
  const pageNumberLimit = 10;

  useEffect(() => {
    fetchSubmissions(currentPage, search, initialSetId, searchDate);
  }, [currentPage]);

  async function fetchSubmissions(
    page = 1,
    searchQuery = search,
    setId = initialSetId,
    date = searchDate,
  ) {
    setLoading(true);
    try {
      const params = { page: page, limit: perPage };
      if (searchQuery) params.search = searchQuery;
      if (setId) params.setId = setId;
      if (date) params.date = date;

      const resp = await axios.get(CONST.ISO_ALL_SUBMISSIONS, {
        headers: { Authorization: `Basic ${token}` },
        params: params,
      });

      if (resp.data && resp.data.status === "success") {
        setSubmissions(resp.data.data || []);
        setTotalRows(resp.data.total_count || resp.data.total || (resp.data.data ? resp.data.data.length : 0));
        setCurrentPage(page);
      } else {
        toast.error(resp.data.message || "Failed to load submissions");
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      toast.error("Error fetching submissions");
    } finally {
      setLoading(false);
    }
  }

  const handleSearch = () => {
    setCurrentPage(1);
    fetchSubmissions(1, search, initialSetId, searchDate);
  };

  const handleResetSearch = () => {
    setSearch("");
    setSearchDate("");
    setCurrentPage(1);
    fetchSubmissions(1, "", initialSetId, "");
  };

  const handleViewResult = (submission) => {
    setSelectedSubmission(submission);
    setShowModal(true);
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => row.first_name,
      sortable: true,
      width: "20%",
      cell: (row) => (
        <div>
          <div className="font-weight-medium">{row.first_name} {row.last_name}</div>
          <div className="text-muted small">{row.email}</div>
        </div>
      ),
    },
    {
      name: "Type",
      selector: (row) => row.type,
      width: "10%",
      center: true,
      cell: (row) => (
        <span className={`badge ${row.type === "Guest" ? "bg-info-lt" : "bg-azure-lt text-azure"}`}>
          {row.type}
        </span>
      ),
    },
    {
      name: "Assignment",
      selector: (row) => row.assignment_name,
      width: "20%",
    },
    {
      name: "Date",
      selector: (row) => row.submission_date,
      width: "15%",
      center: true,
      cell: (row) => (
        row.submission_date ? new Date(row.submission_date).toLocaleDateString("en-GB") : "-"
      ),
    },
    {
      name: "Status",
      selector: (row) => row.status,
      width: "12%",
      center: true,
      cell: (row) => (
        <span className={`db-status-badge ${row.status === "completed" ? "db-badge-success" : "db-badge-warning"}`}>
          {row.status === "completed" ? "Completed" : "Assigned"}
        </span>
      ),
    },
    {
      name: "Score",
      selector: (row) => row.score,
      width: "10%",
      center: true,
      cell: (row) => <span className="fw-bold">{row.score}</span>,
    },
    {
      name: "Actions",
      width: "13%",
      center: true,
      cell: (row) => <ViewIcon tooltip="View Result" onClick={() => handleViewResult(row)} />,
    },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const BootyPagination = ({
    rowsPerPage,
    rowCount,
    onChangePage,
    currentPage,
  }) => {
    const handleBackButtonClick = () => {
      if ((currentPage - 1) % pageNumberLimit === 0) {
        setMaxPageLimit(maxPageLimit - pageNumberLimit);
        setMinPageLimit(minPageLimit - pageNumberLimit);
      }
      onChangePage(currentPage - 1);
    };

    const handleNextButtonClick = () => {
      if (currentPage + 1 > maxPageLimit) {
        setMaxPageLimit(maxPageLimit + pageNumberLimit);
        setMinPageLimit(minPageLimit + pageNumberLimit);
      }
      onChangePage(currentPage + 1);
    };

    const totalPages = Math.ceil(rowCount / rowsPerPage);
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
    const nextDisabled = currentPage === totalPages;
    const previousDisabled = currentPage === 1;

    return (
      <div className="card-footer d-flex justify-content-between align-items-center">
        <p className="m-0 text-muted small">
          Showing page {currentPage} of {totalPages}
        </p>
        <ul className="pagination pagination-sm m-0 ms-auto">
          <li className={`page-item ${previousDisabled ? "disabled" : ""}`}>
            <button className="page-link" onClick={handleBackButtonClick} disabled={previousDisabled}>
              «
            </button>
          </li>
          {pages.map((page) => {
            if (page <= maxPageLimit && page > minPageLimit) {
              return (
                <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                  <button className="page-link" onClick={() => onChangePage(page)}>
                    {page}
                  </button>
                </li>
              );
            }
            return null;
          })}
          <li className={`page-item ${nextDisabled ? "disabled" : ""}`}>
            <button className="page-link" onClick={handleNextButtonClick} disabled={nextDisabled}>
              »
            </button>
          </li>
        </ul>
      </div>
    );
  };

  const CustomLoader = () => (
    <div style={{ padding: "24px" }} className="text-center">
      <DivLoader color="primary" />
    </div>
  );

  return (
    <>
      <Loading loading={fullLoading} background="rgba(0, 0, 0, 0.5)" loaderColor="#fff" />
      <HeaderSection
        title="Admin"
        heading="Quiz Submissions"
      />
      <div className="page-body">
        <div className="container-xl">
          <div className="row row-cards">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Search & Filters</h3>
                </div>
                <div className="card-body border-bottom py-3">
                  <div className="row align-items-end">
                    <div className="col-md-5">
                      <div className="form-group">
                        <label className="form-label">Search Keyword</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Name, Email or Assignment..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label className="form-label">Submission Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={searchDate}
                          onChange={(e) => setSearchDate(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="d-flex gap-2">
                        <button className="btn btn-primary mr-2" onClick={handleSearch}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-search" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
															<path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
															<circle cx="10" cy="10" r="7"></circle>
															<line x1="21" y1="21" x2="15" y2="15"></line>
														</svg>Search</button>&nbsp;
                        <button className="btn btn-outline-primary" onClick={handleResetSearch}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-x" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
															<path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
															<line x1="18" y1="6" x2="6" y2="18"></line>
															<line x1="6" y1="6" x2="18" y2="18"></line>
														</svg>Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <DataTable
                  columns={columns}
                  data={submissions}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  onChangePage={handlePageChange}
                  paginationComponent={BootyPagination}
                  progressPending={loading}
                  progressComponent={<CustomLoader />}
                  noDataComponent={<div className="p-4 text-center">No submissions found.</div>}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <ViewSubmissionModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        submission={selectedSubmission}
      />
    </>
  );
}
